import React from "react";
import Row from "antd/es/row";
import Col from "antd/es/col";
import Button from "antd/es/button";
import Popconfirm from "antd/es/popconfirm";
import Drawer from "antd/es/drawer";
import Card from "antd/es/card";
import Spin from "antd/es/spin";
import notification from "antd/es/notification";
import {
  PhoneFilled,
  AudioMutedOutlined,
  AudioOutlined,
  VideoCameraOutlined,
  EyeInvisibleOutlined,
  MessageOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import useIsMobile from "./utils/useIsMobile";
import ChatFooter from "./components/ChatFooter";
import Chat from "./components/Chat";
import AccCore from "opentok-accelerator-core";
import classNames from "classnames";
import "./Vonage.css";
import "opentok-solutions-css";

const otCoreOptions = (props) => {
  return {
    credentials: props,
    // A container can either be a query selector or an HTML Element
    streamContainers(pubSub, type, data, stream) {
      return {
        publisher: {
          camera: "#cameraPublisherContainer",
          screen: "#screenPublisherContainer",
        },
        subscriber: {
          camera: "#cameraSubscriberContainer",
          screen: "#screenSubscriberContainer",
        },
      }[pubSub][type];
    },
    controlsContainer: "#controls",
    packages: ["screenSharing", "annotation"],
    communication: {
      callProperties: null, // Using default
    },
    screenSharing: {
      extensionID: "plocfffmbcclpdifaikiikgplfnepkpo",
      annotation: true,
      externalWindow: false,
      dev: true,
      screenProperties: {
        insertMode: "append",
        width: "100%",
        height: "100%",
        showControls: false,
        style: {
          buttonDisplayMode: "off",
        },
        videoSource: "window",
        fitMode: "contain", // Using default
      },
    },
    annotation: {
      absoluteParent: {
        publisher: ".App-video-container",
        subscriber: ".App-video-container",
      },
    },
  };
};

const containerClasses = (state) => {
  const { active, meta, localAudioEnabled, localVideoEnabled } = state;
  const sharingScreen = meta ? !!meta.publisher.screen : false;
  const viewingSharedScreen = meta ? meta.subscriber.screen : false;
  const activeCameraSubscribers = meta ? meta.subscriber.camera : 0;
  const activeCameraSubscribersGt2 = activeCameraSubscribers > 2;
  const activeCameraSubscribersOdd = activeCameraSubscribers % 2;
  const screenshareActive = viewingSharedScreen || sharingScreen;
  return {
    controlClass: classNames("App-control-container", { hidden: !active }),
    localAudioClass: classNames("ots-video-control circle audio", {
      hidden: !active,
      muted: !localAudioEnabled,
    }),
    localVideoClass: classNames("ots-video-control circle video", {
      hidden: !active,
      muted: !localVideoEnabled,
    }),
    localCallClass: classNames("ots-video-control circle end-call", {
      hidden: !active,
    }),
    cameraPublisherClass: classNames("video-container", {
      hidden: !active,
      small: !!activeCameraSubscribers || screenshareActive,
      left: screenshareActive,
    }),
    screenPublisherClass: classNames("video-container", {
      hidden: !active || !sharingScreen,
    }),
    cameraSubscriberClass: classNames(
      "video-container",
      { hidden: !active || !activeCameraSubscribers },
      { "active-gt2": activeCameraSubscribersGt2 && !screenshareActive },
      { "active-odd": activeCameraSubscribersOdd && !screenshareActive },
      { small: screenshareActive }
    ),
    screenSubscriberClass: classNames("video-container", {
      hidden: !viewingSharedScreen || !active,
    }),
  };
};

const connectingMask = () => (
  <div className="App-mask">
    <div className="message with-spinner">Connecting</div>
  </div>
);

export default function RoomVonage({ hangup, response, session }) {
  const [audioStatus, setAudioStatus] = React.useState(true);
  const [videoStatus, setVideoStatus] = React.useState(true);
  /* eslint-disable no-unused-vars */
  const [subscriber, setSubscriber] = React.useState(null);
  const [publisher, setPublisher] = React.useState(null);
  /* eslint-enable no-unused-vars */
  const [loading, setLoading] = React.useState(false);
  const [roomChat, setRoomChat] = React.useState(true);
  const [openDrawerChat, setOpenDrawerChat] = React.useState(false);
  const [otCore, setOtCore] = React.useState(null);
  const [connected, setConnected] = React.useState(false);
  const [startScreen, setStartScreen] = React.useState(false);
  const [active, setActive] = React.useState(false);
  const [hasClicked, setHasClicked] = React.useState(false);
  const [meta, setMeta] = React.useState(false);

  const isMobile = useIsMobile();

  React.useEffect(() => {
    let otC = new AccCore(otCoreOptions(session));
    otC
      .connect()
      .then(() => setConnected(true))
      .catch((err) => {
        notification.error({
          message: err.message,
          duration: 5,
          placement: "bottomRight",
        });
      });

    const events = [
      "subscribeToCamera",
      "unsubscribeFromCamera",
      "subscribeToScreen",
      "unsubscribeFromScreen",
      "startScreenShare",
      "endScreenShare",
    ];

    events.forEach((event) =>
      otC.on(event, ({ publishers, subscribers, meta }) => {
        console.log(event);
        if (event === "startScreenShare" || event === "subscribeToScreen") {
          setStartScreen(true);
        }

        if (event === "endScreenShare" || event === "unsubscribeFromScreen") {
          setStartScreen(false);
        }

        setPublisher(publishers);
        setSubscriber(subscribers);
        setMeta(meta);
      })
    );

    setOtCore(otC);
  }, [session]);

  const startCall = async () => {
    await setHasClicked(true);
    otCore?.startCall().then(({ publishers, subscribers, meta }) => {
      setPublisher(publishers);
      setSubscriber(subscribers);
      setMeta(meta);
      setActive(true);
    });
  };

  const endCall = async () => {
    await otCore?.endCall();
    await otCore?.disconnect();
    await setActive(false);
    await setHasClicked(false);
    await hangup();
  };

  const toggleLocalAudio = () => {
    otCore?.toggleLocalAudio(!audioStatus);
    setAudioStatus(!audioStatus);
  };

  const toggleLocalVideo = () => {
    otCore?.toggleLocalVideo(!videoStatus);
    setVideoStatus(!videoStatus);
  };

  const startCallMask = (start) => (
    <div className="App-mask">
      <button
        className="message button clickable"
        onClick={start}
        style={{ cursor: "pointer" }}
        disabled={hasClicked}
      >
        Click to Start Call{" "}
      </button>
    </div>
  );

  const {
    // localAudioClass,
    // localVideoClass,
    //  localCallClass,
    controlClass,
    cameraPublisherClass,
    screenPublisherClass,
    cameraSubscriberClass,
    screenSubscriberClass,
  } = containerClasses({
    active,
    meta,
    localAudioEnabled: audioStatus,
    localVideoEnabled: videoStatus,
  });

  const localRoom = (
    <div
      className="room"
      style={{
        height: "100%",
        padding: 10,
      }}
    >
      {/* <PerfectScrollbar> */}
      <div
        style={{
          width: "100%",
          height: window.innerHeight - 20,
          borderRadius: 8,
          position: "relative",
          overflowX: "hidden",
          overflowY: "hidden",
        }}
        id="room-wrap"
      >
        <div className="App">
          <div className="App-main">
            <div className="App-video-container" id="video_call_body">
              {!connected && connectingMask()}
              {connected && !active && startCallMask(startCall)}
              <div
                id="screenPublisherContainer"
                className={screenPublisherClass}
              />
              <div
                id="screenSubscriberContainer"
                className={screenSubscriberClass}
              />
              <div
                style={
                  startScreen
                    ? {
                        display: "flex",
                        flexDirection: "column",
                        height: "-webkit-fill-available",
                        width: "20%",
                      }
                    : active
                    ? { display: "flex", width: "100%", height: "100%" }
                    : { display: "none" }
                }
              >
                {isMobile ? (
                  <>
                    <div
                      id="cameraPublisherContainer"
                      className={cameraPublisherClass}
                      style={{
                        position: "absolute",
                        bottom: 0,
                        right: 0,
                        zIndex: 9999,
                        width: 150,
                        height: 150,
                        marginRight: 0,
                      }}
                    />
                    {startScreen ? (
                      <div
                        id="cameraSubscriberContainer"
                        className={cameraSubscriberClass}
                        style={{
                          position: "absolute",
                          bottom: 0,
                          right: 0,
                          zIndex: 9999,
                          width: 150,
                          height: 150,
                          marginRight: 0,
                        }}
                      />
                    ) : (
                      <div
                        id="cameraSubscriberContainer"
                        className={cameraSubscriberClass}
                      />
                    )}
                  </>
                ) : (
                  <>
                    <div
                      id="cameraPublisherContainer"
                      className={cameraPublisherClass}
                    />
                    <div
                      id="cameraSubscriberContainer"
                      className={cameraSubscriberClass}
                    />
                  </>
                )}
              </div>
            </div>
            <div id="controls" className={controlClass}>
              <Button
                style={{
                  background: "#434c54",
                  backdropFilter: "blur(3px)",
                  marginRight: 5,
                  color: "#fff",
                }}
                type={audioStatus ? "text" : "danger"}
                danger={!audioStatus}
                icon={!audioStatus ? <AudioMutedOutlined /> : <AudioOutlined />}
                onClick={() => {
                  toggleLocalAudio();
                }}
                shape="circle"
                size="large"
              ></Button>
              <Button
                type={videoStatus ? "text" : "primary"}
                danger={!videoStatus}
                icon={
                  !videoStatus ? (
                    <EyeInvisibleOutlined />
                  ) : (
                    <VideoCameraOutlined />
                  )
                }
                onClick={() => {
                  toggleLocalVideo();
                }}
                shape="circle"
                size="large"
                style={{
                  background: "#434c54",
                  backdropFilter: "blur(3px)",
                  marginRight: 5,
                  color: "#fff",
                }}
              ></Button>
              <Popconfirm
                placement="top"
                title="Anda yakin menutup panggilan?"
                onConfirm={() => {
                  endCall();
                }}
                okText="Iya"
                cancelText="Tidak"
                key="logout"
              >
                <Button
                  danger
                  type="primary"
                  icon={<PhoneFilled style={{ transform: "rotate(225deg)" }} />}
                  size="large"
                  style={{ marginRight: 5 }}
                ></Button>
              </Popconfirm>
              <Button
                type={"text"}
                icon={<MessageOutlined />}
                onClick={() => {
                  if (isMobile) {
                    setOpenDrawerChat(true);
                  } else {
                    if (roomChat) {
                      setRoomChat(false);
                    } else {
                      setRoomChat(true);
                    }
                  }
                }}
                shape="circle"
                size="large"
                style={{
                  background: "#434c54",
                  backdropFilter: "blur(3px)",
                  marginRight: 5,
                  color: "#fff",
                }}
              ></Button>
            </div>
            {/* <div id="chat" className="App-chat-container" /> */}
          </div>
        </div>
      </div>
      {/* </PerfectScrollbar> */}
    </div>
  );

  const cardChat = (height) => (
    <Card
      style={{
        background: window.THEMES.chatRoom,
        border: "unset",
        height: height,
        borderRadius: height === "100%" ? "unset" : "",
      }}
      bordered
      bodyStyle={{
        padding: 0,
        height: "100%",
        borderRadius: height === "100%" ? "unset" : "",
      }}
    >
      <Spin spinning={loading} tip="Sedang memproses..." className="h-full">
        {height === "100%" ? (
          <div style={{ display: "flex", alignItems: "center" }}>
            <div
              style={{
                color: "var(--ant-box-text-color)",
                fontWeight: "bold",
                fontSize: 14,
                padding: "10px 20px 10px 20px",
                height: "5%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              Pesan dalam panggilan
            </div>
            <div style={{ marginLeft: "auto", marginRight: 10 }}>
              <Button
                type="text"
                icon={<CloseOutlined className="footer-icon-color" />}
                onClick={() => setOpenDrawerChat(false)}
              />
            </div>
          </div>
        ) : (
          <div
            style={{
              color: "var(--ant-box-text-color)",
              fontWeight: "bold",
              fontSize: 14,
              padding: "10px 20px 10px 20px",
              height: "5%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            Pesan dalam panggilan
          </div>
        )}

        <div style={{ height: "75%" }}>
          <Chat
            room={{
              name: response?.room?.uniqueName,
            }}
            setLoading={setLoading}
          />
        </div>
        <div
          style={{
            height: "15%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <ChatFooter
            room={{
              name: response?.room?.uniqueName,
            }}
            response={response}
            setLoading={setLoading}
            loading={loading}
          />
        </div>
      </Spin>
    </Card>
  );

  return (
    <div
      style={{
        minHeight: "100%",
        minWidth: "100%",
        // background: window.THEMES.bgColor,
      }}
    >
      <Row
        //gutter={[12, 12]}
        style={{ height: "100%" }}
        justify="center"
        align={"middle"}
      >
        <Col
          xs={24}
          sm={24}
          md={24}
          lg={roomChat ? 18 : 24}
          xl={roomChat ? 18 : 24}
          style={{ height: "100%" }}
        >
          {localRoom}
        </Col>
        <Col
          xs={0}
          sm={0}
          md={0}
          lg={roomChat ? 6 : 0}
          xl={roomChat ? 6 : 0}
          style={{ padding: 10 }}
        >
          {cardChat(window.innerHeight - 20)}
        </Col>
      </Row>
      <Drawer
        width={"100%"}
        bodyStyle={{ background: "transparent", padding: 0 }}
        closable={false}
        placement="left"
        // onClose={() => setOpenDrawerChat(false)}
        open={openDrawerChat}
      >
        {cardChat("100%")}
      </Drawer>
    </div>
  );
}
